// ============================================================
// SV Audio Player
// ============================================================
import { initVisualizer } from "./visualizer.js";
import {
  buildAppConfig,
  initPageFromConfig,
  syncAlbumActiveToCurrentAudio,
  initAlbumTrackClickPlayback,
} from "./audio-player-page-config.js";
import {
  updateDrawerFromTrackUi,
  updateNowPlayingShortcut,
  initDesktopBar,
  openDownloadModal,
  renderDrawerPlaylist,
  initDrawerInteractions,
} from "./audio-player-ui-drawer.js";
import {
  refreshMobileTitleMarquee,
  initMobileBar,
} from "./audio-player-ui-mobile.js";

import { reinitLyricsSyncForCurrentPage } from "./audio-player-bridges.js";

export {
  initPageFromConfig,
  syncAlbumActiveToCurrentAudio,
  initAlbumTrackClickPlayback,
  reinitLyricsSyncForCurrentPage,
};

// Keep a single active lyrics controller so we can tear it down on page change
// let __lyricsSyncController = null;

// --- Slim Volume debug helper ---
// Toggle in the browser console: window.__SV_DEBUG__ = true
function svDebug(tag, payload) {
  try {
    const enabled =
      typeof window !== "undefined" && window.__SV_DEBUG__ === true;
    if (!enabled) return;
    // eslint-disable-next-line no-console
    console.log("[SVDEBUG]", tag, payload);
  } catch {
    // never throw from debug
  }
}
// --- end debug helper ---

// 0. Global wiring & shared constants
// ------------------------------------------------------------
// Gently warm up the audio so first play is snappier
function warmPreloadAudio(audioElement) {
  if (!audioElement) return;

  // Some safety around older browsers
  const MediaCtor = window.HTMLMediaElement;
  const HAVE_ENOUGH_DATA =
    MediaCtor && typeof MediaCtor.HAVE_ENOUGH_DATA === "number"
      ? MediaCtor.HAVE_ENOUGH_DATA
      : 4; // spec value

  try {
    // If we already have enough buffered, don't touch it
    if (audioElement.readyState >= HAVE_ENOUGH_DATA) {
      return;
    }
  } catch {
    // Non-fatal; we'll just try a gentle preload below
  }

  // Light-touch: schedule after initial paint so we don't block load
  window.setTimeout(() => {
    try {
      if (!audioElement.src) return;

      // 🛑 New: if the user has already started playback, do nothing.
      // Calling load() mid-playback will reset/stop the audio.
      if (!audioElement.paused || audioElement.currentTime > 0) {
        return;
      }

      // Hint the browser to grab more than just metadata
      audioElement.preload = "auto";

      // This just nudges the browser to fetch more data;
      // with the guards above it won't fire once playback has begun.
      audioElement.load();
      // console.log('[Player] Warm preload kicked in for audioElement');
    } catch (err) {
      console.warn("[Player] Preload warm-up failed:", err);
    }
  }, 1000);
}

function formatTime(seconds) {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

function handleSeekFromPoint(point, seekBar, audioElement, seekProgress) {
  if (!seekBar || !audioElement || !audioElement.duration) return;
  if (typeof point?.clientX !== "number") return;

  const rect = seekBar.getBoundingClientRect();
  if (!rect || !rect.width) return;

  let percent = (point.clientX - rect.left) / rect.width;
  percent = Math.max(0, Math.min(1, percent));

  const newTime = percent * audioElement.duration;
  if (!Number.isFinite(newTime)) return;

  audioElement.currentTime = newTime;

  if (seekProgress) {
    seekProgress.style.width = `${percent * 100}%`;
  }

  seekBar.setAttribute("aria-valuenow", String(Math.round(percent * 100)));
}

function wrapSeekBar() {
  const seek = document.querySelector(".seek-bar-container");
  const time = document.querySelector(".time-display");

  if (!seek || !time) return;
  if (document.querySelector(".seek-wrapper")) return;

  const wrapper = document.createElement("div");
  wrapper.className = "seek-wrapper";

  if (!seek.parentNode) return;

  seek.parentNode.insertBefore(wrapper, seek);
  wrapper.appendChild(seek);
  wrapper.appendChild(time);
}

function initDrawerActions(rootEl, trackModel) {
  if (!rootEl || !trackModel) return;

  const links = trackModel.links || {};
  const availability = trackModel.availability || { canDownload: !!links.mp3 };

  const buyEl = rootEl.querySelector(".drawer-action-buy");
  const downloadEl = rootEl.querySelector(".drawer-action-download");

  // Purchase: prefer store → bandcamp → website, or hide if nothing
  const storeHref = links.store || links.bandcamp || links.website || null;

  if (buyEl) {
    if (storeHref) {
      buyEl.href = storeHref;
      buyEl.style.display = "";
    } else {
      buyEl.style.display = "none";
    }
  }

  // Download: only if canDownload is true and we have an mp3 link
  const mp3Href = links.mp3 || null;
  const canDownload = availability.canDownload ?? !!mp3Href;

  if (!downloadEl) return;

  if (!canDownload || !mp3Href) {
    downloadEl.style.display = "none";
    return;
  }

  // Route through shared modal (no direct download)
  downloadEl.style.display = "";
  downloadEl.addEventListener("click", (event) => {
    event.preventDefault();
    openDownloadModal({ storeHref, mp3Href });
  });
}

export function createPlayerApp(rootEl, config = {}) {
  try {
    const audioPlayer = rootEl;
    if (!audioPlayer) {
      console.warn(
        "[Player] createPlayerApp called without a valid root element; aborting init."
      );
      return null;
    }

    // 🔒 Per-root guard: only init once per #audioPlayer
    if (audioPlayer.__svPlayerInitialized) {
      console.warn(
        "[Player] Player already initialized for this root; skipping re-init."
      );
      const existingAudio = document.getElementById("audioElement");
      return existingAudio ? { audioElement: existingAudio } : null;
    }
    audioPlayer.__svPlayerInitialized = true;

    // NEW: pull in JSON config from <script type="application/json">
    const appConfig = buildAppConfig(audioPlayer, config);

    // Preserve existing has-audio behavior:
    // 1) data-has-audio attribute is still the primary truth
    // 2) fall back to appConfig.audioUrl / appConfig.hasAudio if needed
    const hasAudioAttr = audioPlayer.dataset?.hasAudio;
    const hasAudio =
      hasAudioAttr === "true" ||
      (hasAudioAttr == null &&
        (appConfig.hasAudio ?? Boolean(appConfig.audioUrl)));

    if (!hasAudio) {
      console.warn(
        "[Player] No initial audio file; initializing player shell anyway. " +
          "A later page config can provide a track/playlist."
      );
    }

    const audioElementId =
      appConfig.audioElementId || config.audioElementId || "audioElement";
    const audioElement = document.getElementById(audioElementId);
    if (!audioElement) {
      console.error(
        "[Player] audioElement not found in the DOM; cannot initialize player."
      );
      return null;
    }

    const desktopTitleSlot = audioPlayer.querySelector(
      "[data-sv-player-title]"
    );

    function updateDesktopTitleFromTrack(track) {
      // Re-query every time in case layout / DOM changed
      const slots = audioPlayer.querySelectorAll("[data-sv-player-title]");

      // Canonical: whatever SVPlayer thinks is current
      let canonicalTrack = null;
      if (
        window.SVPlayer &&
        typeof window.SVPlayer.getCurrentTrack === "function"
      ) {
        canonicalTrack = window.SVPlayer.getCurrentTrack();
      }

      // Prefer canonical; fall back to the passed-in track only if needed
      let effectiveTrack = canonicalTrack || track || null;

      console.log("[SVPlayer] updateDesktopTitleFromTrack", {
        requestedId: track && track.id,
        canonicalId: canonicalTrack && canonicalTrack.id,
        finalId: effectiveTrack && effectiveTrack.id,
      });

      if (!slots.length || !effectiveTrack) {
        return;
      }

      const title =
        effectiveTrack.songTitle ||
        effectiveTrack.title ||
        effectiveTrack.name ||
        effectiveTrack.id ||
        "";

      console.log("[SVPlayer] desktop title set to", title);

      slots.forEach((el) => {
        el.textContent = title || "";
      });

      // After titles update, recalc whether mobile title should marquee
      refreshMobileTitleMarquee();
    }

    const desktopArtSlot = audioPlayer.querySelector("[data-sv-player-art]");
    function updateDesktopArtFromTrack(track) {
      // Re-query each time so it works after layout changes / PJAX
      const slots = audioPlayer.querySelectorAll("[data-sv-player-art]");
      if (!slots.length) return;

      // Prefer the canonical SVPlayer track
      let canonicalTrack = null;
      if (
        window.SVPlayer &&
        typeof window.SVPlayer.getCurrentTrack === "function"
      ) {
        canonicalTrack = window.SVPlayer.getCurrentTrack();
      }

      const effectiveTrack = canonicalTrack || track || null;
      if (!effectiveTrack) return;

      // --- HERE is the key bit: derive album artwork path from audioUrl ---

      // 1) Get an audio URL we can parse
      const audioUrl =
        effectiveTrack.audioUrl ||
        (effectiveTrack.links && effectiveTrack.links.mp3) ||
        null;

      let artUrl = null;

      if (audioUrl) {
        // Strip any ?query or #hash
        const clean = audioUrl.split(/[?#]/)[0];

        // Expect something like:
        //   /music/albums/staring-at-the-sun/mp3/july.mp3
        const mp3Index = clean.lastIndexOf("/mp3/");

        if (mp3Index !== -1) {
          // Everything before "/mp3/" is the album root:
          //   /music/albums/staring-at-the-sun
          const albumRoot = clean.slice(0, mp3Index);

          // Build the artwork path you want:
          //   /music/albums/staring-at-the-sun/artwork/cover.webp
          artUrl = `${albumRoot}/artwork/cover.webp`;
        }
      }

      // Fallback if for some reason we couldn't derive it
      if (!artUrl) {
        artUrl =
          effectiveTrack.artUrl ||
          effectiveTrack.image ||
          "/images/default-artwork.jpg";
      }

      const artAlt =
        effectiveTrack.artAlt ||
        effectiveTrack.songTitle ||
        effectiveTrack.title ||
        effectiveTrack.name ||
        effectiveTrack.id ||
        "";

      // Update all art slots (desktop container + mobile <img>)
      slots.forEach((slot) => {
        if (!slot) return;

        const isImg = slot.tagName && slot.tagName.toLowerCase() === "img";

        if (!artUrl) {
          if (isImg) {
            slot.removeAttribute("src");
            slot.alt = "";
          } else {
            slot.textContent = "";
          }
          return;
        }

        if (isImg) {
          // Mobile bar: slot is already an <img>
          slot.src = artUrl;
          slot.alt = artAlt;
          slot.loading = "lazy";
          slot.decoding = "async";
        } else {
          // Desktop bar: slot is a container div
          slot.textContent = "";

          const img = document.createElement("img");
          img.src = artUrl;
          img.alt = artAlt;
          img.className = "ui-artwork";
          img.loading = "lazy";
          img.decoding = "async";

          slot.appendChild(img);
        }
      });
    }

    // OPTIONAL: internal track metadata (not used yet, just ready for future features)
    const trackModel = {
      id: appConfig.id || null,
      title:
        appConfig.songTitle ||
        document.getElementById("songTitle")?.textContent?.trim() ||
        "",
      albumTitle: appConfig.albumTitle || "",
      artUrl:
        appConfig.artUrl || document.getElementById("albumArt")?.src || "",
      artAlt:
        appConfig.artAlt || document.getElementById("albumArt")?.alt || "",
      description: appConfig.description || "",
      lyrics: appConfig.lyrics || null,
      links: appConfig.links || {},

      // 🔹 New: availability flags (with a safe default)
      availability: appConfig.availability || {
        // Fallback: if no explicit availability is provided,
        // assume we can download if we have an mp3 link.
        canDownload: !!(appConfig.links && appConfig.links.mp3),
      },
    };

    // If you want to sanity-check this in dev:
    console.log("[Player] trackModel:", trackModel);

    // Gently warm up the audio so first play is snappier
    warmPreloadAudio(audioElement);

    // Track actions in the drawer (purchase / download)
    const drawerActionsRoot = document.getElementById("playerDrawerActions");
    if (drawerActionsRoot) {
      initDrawerActions(drawerActionsRoot, trackModel);
    }

    // -----------------------------
    // Lyrics sync (main article)
    // -----------------------------
    try {
      reinitLyricsSyncForCurrentPage(audioElement);
    } catch (err) {
      console.error("[Lyrics] Failed to initialize lyrics sync:", err);
    }

    // -----------------------------
    // Visualizer wiring
    // -----------------------------
    const canvasElem = document.getElementById("visualizerCanvas");
    let visualizerController = null; // object from initVisualizer, or null

    /**
     * Ensure the visualizer audio graph is initialized exactly once.
     * We do this on first playback so later toggles don't touch audio routing.
     */
    function ensureVisualizerInit() {
      if (visualizerController || !canvasElem) return;
      try {
        visualizerController = initVisualizer(audioElement, canvasElem);
        console.log("[Visualizer] Initialized visualizer audio graph.");
      } catch (err) {
        console.error("[Visualizer] Failed to initialize visualizer:", err);
        visualizerController = null;
      }
    }

    // -----------------------------
    // Core player controls / UI
    // -----------------------------
    // const expandHandle = document.getElementById("expandHandle");
    // if (expandHandle) {
    //   expandHandle.addEventListener("click", (event) => {
    //     console.log("[SVPlayer] expandHandle clicked", {
    //       targetTag: event.target.tagName,
    //       targetClass: event.target.className,
    //     });

    //     toggleExpanded("handle");
    //   });
    // }
    const expandHandle = document.getElementById("expandHandle");
    const playPauseBtn = document.getElementById("playPauseBtn");
    const playIcon = document.getElementById("playIcon");
    const pauseIcon = document.getElementById("pauseIcon");

    const prevBtn = document.getElementById("prevBtn");
    const nextBtn = document.getElementById("nextBtn");

    const seekBar = document.getElementById("seekBar");
    const seekProgress = document.getElementById("seekProgress");

    const currentTimeEl = document.getElementById("currentTime");
    const durationEl = document.getElementById("duration");

    let isPlaying = false;
    let isSeeking = false;

    ///music hard reload → no playlist, no src → button disabled.
    // Hard reload on album/song page → playlist or track present → button enabled.
    syncPlayButtonEnabledState();

    // -----------------------------
    // Visibility / background handling (mobile-friendly)
    // -----------------------------

    // let wasPlayingOnHide = false;

    function syncPlayButtonUI(playing) {
      isPlaying = playing;
      if (!playPauseBtn || !playIcon || !pauseIcon) return;

      if (playing) {
        playIcon.style.display = "none";
        pauseIcon.style.display = "block";
        playPauseBtn.setAttribute("aria-label", "Pause");
      } else {
        playIcon.style.display = "block";
        pauseIcon.style.display = "none";
        playPauseBtn.setAttribute("aria-label", "Play");
      }
    }

    //     function handleVisibilityChange() {
    //       if (!audioElement) return;

    //       if (document.visibilityState === 'hidden') {
    //         // Remember state, then pause so time doesn't "run" while you're away
    //         wasPlayingOnHide = !audioElement.paused;

    //         if (wasPlayingOnHide) {
    //           audioElement.pause();
    //           syncPlayButtonUI(false);
    //         }
    //  } else if (document.visibilityState === 'visible') {
    //       // You can later restore auto-resume here if you want
    //       // if (wasPlayingOnHide) { audioElement.play(); }
    //       wasPlayingOnHide = false;
    //     }
    //   }

    // 🆕 Prevent multiple visibility listeners across any future inits
    // if (!document.__svVisibilityHandlerAttached) {
    //   document.addEventListener('visibilitychange', handleVisibilityChange);
    //   document.__svVisibilityHandlerAttached = true;
    // }

    let isExpanded = audioPlayer.classList.contains("expanded");

    // Match your CSS transform duration (~0.4s)
    const EXPAND_ANIM_MS = 400;
    let isAnimatingExpand = false;

    function applyExpandedState() {
      audioPlayer.classList.toggle("expanded", isExpanded);

      if (expandHandle) {
        expandHandle.setAttribute("aria-expanded", String(isExpanded));

        const srLabel = expandHandle.querySelector(".sr-only");
        if (srLabel) {
          srLabel.textContent = isExpanded
            ? "Collapse Player"
            : "Expand Player";
        }
      }
    }

    // Returns true once the player actually has a loaded track
    function hasLoadedTrack() {
      if (!audioElement) return false;
      const src = audioElement.currentSrc || audioElement.src || "";
      return src.trim() !== "";
    }

    //UI knows when to enable/disable the play button.
    function syncPlayButtonEnabledState() {
      const sv = window.SVPlayer;

      // "Playable" means: either we have a loaded src OR a playlist.
      const hasPlaylist =
        !!sv &&
        typeof sv.getPlaylistLength === "function" &&
        sv.getPlaylistLength() > 0;

      const enabled = hasLoadedTrack() || hasPlaylist;

      if (playPauseBtn) {
        // For real <button> elements, this actually prevents click events.
        if ("disabled" in playPauseBtn) {
          playPauseBtn.disabled = !enabled;
        }

        playPauseBtn.setAttribute("aria-disabled", enabled ? "false" : "true");
        playPauseBtn.classList.toggle("is-disabled", !enabled);
      }
    }

    function toggleExpanded(source = "unknown") {
      // Do nothing until a track has actually been loaded into the player
      if (!hasLoadedTrack()) {
        console.log("[SVPlayer] Drawer toggle ignored (no track loaded yet)", {
          source,
        });
        return;
      }

      // Guard: while we're animating, ignore extra toggles
      if (isAnimatingExpand) {
        // console.log('[Player] Ignoring toggle from', source, 'while animating');
        return;
      }

      isAnimatingExpand = true;
      isExpanded = !isExpanded;

      // console.log('[Player] toggleExpanded:', { source, isExpanded });

      applyExpandedState();

      // Release the lock after the CSS animation should be done
      window.setTimeout(() => {
        isAnimatingExpand = false;
      }, EXPAND_ANIM_MS + 80);
    }

    // Initial sync
    applyExpandedState();

    // -----------------------------
    // Expand handle: desktop-only click + keyboard
    // -----------------------------
    if (expandHandle) {
      const HANDLE_DEBOUNCE_MS = 300;
      let lastHandleToggleTime = 0;

      const triggerHandleToggle = (source) => {
        const now =
          window.performance && performance.now
            ? performance.now()
            : Date.now();

        if (now - lastHandleToggleTime < HANDLE_DEBOUNCE_MS) {
          return;
        }
        lastHandleToggleTime = now;

        toggleExpanded(source);
      };

      expandHandle.addEventListener("click", (event) => {
        // On mobile layout, let the mobile bar handle taps instead
        if (window.innerWidth <= 768) {
          return;
        }

        event.stopPropagation();
        triggerHandleToggle("handle-click");
      });

      // Keyboard accessibility (desktop)
      expandHandle.addEventListener("keydown", (event) => {
        if (event.key === " " || event.key === "Enter") {
          event.preventDefault();
          triggerHandleToggle("handle-keyboard");
        }
      });
    }

    // -----------------------------
    // Play / Pause
    // -----------------------------
    if (playPauseBtn && playIcon && pauseIcon) {
      playPauseBtn.addEventListener("click", () => {
        const sv = window.SVPlayer;

        // Prefer the global SVPlayer API if available
        if (
          sv &&
          typeof sv.play === "function" &&
          typeof sv.pause === "function" &&
          typeof sv.isPlaying === "function"
        ) {
          const currentlyPlaying = sv.isPlaying();

          if (currentlyPlaying) {
            sv.pause();
            // UI will also be updated by the 'pause' event, but this keeps it snappy
            syncPlayButtonUI(false);
            return;
          }

          // Going from paused -> playing.
          ensureVisualizerInit();

          try {
            const playResult = sv.play();

            if (playResult && typeof playResult.then === "function") {
              playResult
                .then(() => {
                  syncPlayButtonUI(true);
                })
                .catch((err) => {
                  console.error("[Player] SVPlayer.play() was rejected:", err);
                  syncPlayButtonUI(false);
                });
            } else {
              syncPlayButtonUI(true);
            }
          } catch (err) {
            console.error("[Player] Error while calling SVPlayer.play():", err);
            syncPlayButtonUI(false);
          }

          return;
        }

        // Fallback: original behaviour if SVPlayer somehow isn't there
        if (isPlaying) {
          audioElement.pause();
          syncPlayButtonUI(false);
          return;
        }

        ensureVisualizerInit();

        try {
          const playResult = audioElement.play();
          if (playResult && typeof playResult.then === "function") {
            playResult
              .then(() => {
                syncPlayButtonUI(true);
              })
              .catch((err) => {
                console.error(
                  "[Player] audioElement.play() was rejected:",
                  err
                );
                syncPlayButtonUI(false);
              });
          } else {
            syncPlayButtonUI(true);
          }
        } catch (err) {
          console.error(
            "[Player] Error while calling audioElement.play():",
            err
          );
          syncPlayButtonUI(false);
        }
      });
    }

    // 🆕 Keep UI in sync with any play/pause, no matter where it came from
    audioElement.addEventListener("play", () => {
      syncPlayButtonUI(true);

      // 🔹 Whenever playback starts (from anywhere), sync "now playing" UI
      if (
        window.SVPlayer &&
        typeof window.SVPlayer.syncNowPlayingUi === "function"
      ) {
        window.SVPlayer.syncNowPlayingUi();
      }
    });

    audioElement.addEventListener("pause", () => {
      syncPlayButtonUI(false);
    });

    audioElement.addEventListener("ended", () => {
      // When the track finishes, treat it like paused
      syncPlayButtonUI(false);
    });

    // -----------------------------
    // Previous / Next
    // -----------------------------
    if (prevBtn) {
      prevBtn.addEventListener("click", (event) => {
        event.preventDefault();

        if (window.SVPlayer && typeof window.SVPlayer.previous === "function") {
          window.SVPlayer.previous();
        }
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener("click", (event) => {
        event.preventDefault();

        if (window.SVPlayer && typeof window.SVPlayer.next === "function") {
          window.SVPlayer.next();
        }
      });
    }

    // -----------------------------
    // Metadata (duration)
    // -----------------------------
    audioElement.addEventListener("loadedmetadata", () => {
      if (durationEl && !Number.isNaN(audioElement.duration)) {
        durationEl.textContent = formatTime(audioElement.duration);
      }
    });

    // -----------------------------
    // Timeupdate + progress bar
    // -----------------------------
    audioElement.addEventListener("timeupdate", () => {
      if (!audioElement.duration || !seekProgress || !seekBar || !currentTimeEl)
        return;

      const current = audioElement.currentTime;
      const duration = audioElement.duration;
      if (!duration || Number.isNaN(duration)) return;

      let progress = (current / duration) * 100;
      if (!Number.isFinite(progress)) return;

      progress = Math.max(0, Math.min(100, progress));

      seekProgress.style.width = `${progress}%`;
      currentTimeEl.textContent = formatTime(current);
      seekBar.setAttribute("aria-valuenow", String(Math.round(progress)));
    });

    // -----------------------------
    // Seek: click + drag
    // -----------------------------
    if (seekBar) {
      seekBar.addEventListener("click", (e) => {
        if (!audioElement.duration) return;
        handleSeekFromPoint(e, seekBar, audioElement, seekProgress);
      });

      seekBar.addEventListener("mousedown", (e) => {
        if (!audioElement.duration) return;
        isSeeking = true;
        handleSeekFromPoint(e, seekBar, audioElement, seekProgress);
      });

      document.addEventListener("mousemove", (e) => {
        if (!isSeeking) return;
        handleSeekFromPoint(e, seekBar, audioElement, seekProgress);
      });

      document.addEventListener("mouseup", () => {
        if (isSeeking) isSeeking = false;
      });

      seekBar.addEventListener(
        "touchstart",
        (e) => {
          if (!audioElement.duration) return;
          if (!e.touches || !e.touches[0]) return;
          isSeeking = true;
          const touch = e.touches[0];
          handleSeekFromPoint(touch, seekBar, audioElement, seekProgress);
        },
        { passive: true }
      );

      document.addEventListener(
        "touchmove",
        (e) => {
          if (!isSeeking) return;
          if (!e.touches || !e.touches[0]) return;
          const touch = e.touches[0];
          handleSeekFromPoint(touch, seekBar, audioElement, seekProgress);
        },
        { passive: true }
      );

      document.addEventListener("touchend", () => {
        if (isSeeking) isSeeking = false;
      });
    } else {
      console.warn("[Player] No seekBar found; seeking will be disabled.");
    }

    // Visualizer On/Off toggle (desktop)
    // -----------------------------
    const vizToggleBtn = document.getElementById("vizToggleBtn");
    let vizEnabled = false;

    if (vizToggleBtn) {
      const updateVizToggleUI = () => {
        vizToggleBtn.classList.toggle("is-on", vizEnabled);
        vizToggleBtn.classList.toggle("is-off", !vizEnabled);

        // switch semantics
        vizToggleBtn.setAttribute("aria-checked", String(vizEnabled));

        const labelSpan = vizToggleBtn.querySelector(".viz-toggle-text");
        if (labelSpan) {
          labelSpan.textContent = vizEnabled
            ? "Visualizer On"
            : "Visualizer Off";
        }

        // 🔹 Update the preset name text in the drawer
        const presetNameEl = document.getElementById("vizPresetName");
        if (presetNameEl) {
          if (!vizEnabled) {
            // Visualizer is off
            presetNameEl.textContent = "Off";
          } else {
            // Read the last preset name from the global set by visualizer.js
            let name = "";
            if (typeof window !== "undefined") {
              name = window.__SV_VIS_LAST_PRESET_NAME || "";
            }
            presetNameEl.textContent = name || "Default";
          }
        }
      };

      console.log(
        "[Drawer] vizEnabled:",
        vizEnabled,
        "presetName from window:",
        window.__SV_VIS_LAST_PRESET_NAME
      );

      // start in OFF state
      updateVizToggleUI();

      vizToggleBtn.addEventListener("click", () => {
        vizEnabled = !vizEnabled;

        if (vizEnabled) {
          // make sure visualizer is initialized
          ensureVisualizerInit();

          if (!visualizerController) {
            console.warn(
              "[Visualizer] Toggle ON requested, but visualizer could not be initialized."
            );
            vizEnabled = false;
            updateVizToggleUI();
            return;
          }

          if (typeof visualizerController.setEnabled === "function") {
            try {
              visualizerController.setEnabled(true);
            } catch (err) {
              console.error("[Visualizer] Error enabling visualizer:", err);
              vizEnabled = false;
            }
          }
        } else {
          if (
            visualizerController &&
            typeof visualizerController.setEnabled === "function"
          ) {
            try {
              visualizerController.setEnabled(false);
            } catch (err) {
              console.error("[Visualizer] Error disabling visualizer:", err);
            }
          }
        }

        // Always refresh UI (including preset name) after toggle
        updateVizToggleUI();
      });
    }

    // -----------------------------
    // Init desktop OR mobile layout on load
    // -----------------------------
    let currentLayout = window.innerWidth >= 769 ? "desktop" : "mobile";
    console.log("[Player] Initial layout:", currentLayout);

    if (currentLayout === "desktop") {
      initDesktopBar({
        prevBtn,
        playPauseBtn,
        nextBtn,
        links: trackModel.links,
        availability: trackModel.availability,
      });
    } else {
      initMobileBar({
        expandHandle,
        prevBtn,
        playPauseBtn,
        nextBtn,
        toggleExpanded,
      });
    }

    // Auto-collapse drawer on mobile when navigating to a song page from the drawer
    // const drawerRoot = document.querySelector("[data-sv-player-drawer]");
    // if (drawerRoot && typeof toggleExpanded === "function") {
    //   drawerRoot.addEventListener("click", (event) => {
    //     const link =
    //       event.target.closest(".drawer-track-link") ||
    //       event.target.closest("[data-sv-now-playing-link]");
    //     if (!link) return;

    //     const isMobileViewport = window.innerWidth < 769;
    //     if (!isMobileViewport) return;

    //     if (audioPlayer.classList.contains("expanded")) {
    //       toggleExpanded("drawer-link-click");
    //     }
    //   });
    // }

    // -----------------------------
    // Auto-collapse drawer on track link click (mobile only)
    // -----------------------------
    const drawerRoot = document.querySelector("[data-sv-player-drawer]");
    if (drawerRoot) {
      drawerRoot.addEventListener("click", (event) => {
        // Only react to clicks on track links inside the drawer
        const link = event.target.closest(".drawer-track-link");
        if (!link || !drawerRoot.contains(link)) return;

        // Mobile-only behavior
        if (window.innerWidth >= 769) return;

        // If we're not expanded, nothing to do
        if (!isExpanded) return;

        // Force-collapse the drawer
        isExpanded = false;
        applyExpandedState();
      });
    }

    // -----------------------------
    // Auto-collapse on "Now playing" shortcut click (mobile only)
    // -----------------------------
    const nowPlayingShortcut = document.querySelector(
      "[data-sv-now-playing-shortcut]"
    );
    if (nowPlayingShortcut) {
      nowPlayingShortcut.addEventListener("click", (event) => {
        const link = event.target.closest(
          ".drawer-nowplaying-link, [data-sv-now-playing-link]"
        );
        if (!link) return;

        // Mobile-only behavior
        if (window.innerWidth >= 769) return;

        // If drawer isn't expanded, nothing to do
        if (!isExpanded) return;

        // Force-collapse the drawer
        isExpanded = false;
        applyExpandedState();
      });
    }

    updateAspectMode(audioPlayer);

    // -----------------------------
    // Responsive layout switching on resize
    // -----------------------------
    let resizeTimeout = null;

    function switchToDesktop() {
      console.log("[Player] Switching to DESKTOP layout");
      const controlsContainer = document.querySelector(".player-controls");
      const controls = document.querySelector(".controls");
      if (!controlsContainer || !controls) {
        console.warn(
          "[Player] switchToDesktop: missing .player-controls or .controls"
        );
        return;
      }

      try {
        const mobileBar = document.querySelector(".mobile-bar");
        if (mobileBar) {
          console.log("[Player] Removing mobile bar for desktop layout");
          mobileBar.remove();
        }

        if (prevBtn && !controls.contains(prevBtn)) {
          console.log("[Player] Reattaching prevBtn to .controls");
          controls.appendChild(prevBtn);
        }
        if (playPauseBtn && !controls.contains(playPauseBtn)) {
          console.log("[Player] Reattaching playPauseBtn to .controls");
          controls.appendChild(playPauseBtn);
        }
        if (nextBtn && !controls.contains(nextBtn)) {
          console.log("[Player] Reattaching nextBtn to .controls");
          controls.appendChild(nextBtn);
        }

        if (expandHandle) {
          expandHandle.classList.remove("mobile-expand-handle");

          if (expandHandle.parentNode !== audioPlayer) {
            console.log("[Player] Reattaching expandHandle to #audioPlayer");
            audioPlayer.appendChild(expandHandle);
          }
        }

        const existingDesktopBar = document.querySelector(".desktop-bar");
        const controlsContainerHasControls =
          controlsContainer.contains(controls);

        if (existingDesktopBar) {
          console.log("[Player] Removing existing desktop bar before rebuild");
          const desktopControls =
            existingDesktopBar.querySelector(".desktop-controls");
          if (
            desktopControls &&
            desktopControls.contains(controls) &&
            !controlsContainerHasControls
          ) {
            controlsContainer.appendChild(controls);
          }
          existingDesktopBar.remove();
        }

        initDesktopBar({
          prevBtn,
          playPauseBtn,
          nextBtn,
          links: trackModel.links,
          availability: trackModel.availability,
        });
      } catch (err) {
        console.error("[Player] Error during switchToDesktop:", err);
      }
    }

    function switchToMobile() {
      console.log("[Player] Switching to MOBILE layout");
      const controlsContainer = document.querySelector(".player-controls");
      const controls = document.querySelector(".controls");
      if (!controlsContainer || !controls) {
        console.warn(
          "[Player] switchToMobile: missing .player-controls or .controls"
        );
        return;
      }

      try {
        const desktopBar = document.querySelector(".desktop-bar");
        if (desktopBar) {
          const desktopControls = desktopBar.querySelector(".desktop-controls");
          if (desktopControls && desktopControls.contains(controls)) {
            controlsContainer.appendChild(controls);
          }
          desktopBar.remove();
        }

        initMobileBar({
          expandHandle,
          prevBtn,
          playPauseBtn,
          nextBtn,
          toggleExpanded,
        });
      } catch (err) {
        console.error("[Player] Error during switchToMobile:", err);
      }
    }

    function updateAspectMode(playerRoot) {
      if (!playerRoot) return;
      const viewportRatio = window.innerWidth / window.innerHeight;
      const isUltraWide = viewportRatio > 2.0;
      playerRoot.classList.toggle("screen-ultrawide", isUltraWide);
    }

    window.addEventListener("resize", () => {
      if (resizeTimeout) clearTimeout(resizeTimeout);

      resizeTimeout = setTimeout(() => {
        const shouldBeDesktop = window.innerWidth >= 769;

        if (shouldBeDesktop && currentLayout !== "desktop") {
          console.log("[Player] Resize breakpoint crossed → desktop");
          switchToDesktop();
          currentLayout = "desktop";
        } else if (!shouldBeDesktop && currentLayout !== "mobile") {
          console.log("[Player] Resize breakpoint crossed → mobile");
          switchToMobile();
          currentLayout = "mobile";
        }

        updateAspectMode(audioPlayer);

        // Re-check mobile marquee after layout/width change
        if (window.innerWidth < 769) {
          refreshMobileTitleMarquee();
        }

        updateAspectMode(audioPlayer);
      }, 150);
    });

    // Minimal public surface for now; can be extended later
    const app = {
      rootEl: audioPlayer,
      audioElement,
      trackModel,
      toggleExpanded,
      updateDesktopTitleFromTrack,
      updateDesktopArtFromTrack,
      syncPlayButtonEnabledState,
    };

    // Expose for debugging / scripting in the browser console.
    // Safe in production; it's just a reference.
    if (typeof window !== "undefined") {
      window.slimVolumePlayer = app;
    }

    return app;
  } catch (err) {
    console.error("[Player] Fatal error during player initialization:", err);
    return null;
  }
}

export function ensureGlobalPlayer() {
  /**
   * Ensure there is a global player bound to #audioPlayer / #audioElement
   * if the DOM for a real player is present.
   *
   * Safe to call multiple times (e.g. from svPageInit after PJAX).
   */
  const root = document.getElementById("audioPlayer");
  if (!root) {
    console.debug("[SVPlayer] ensureGlobalPlayer: no #audioPlayer root.");
    return null;
  }

  // If we already have a working global player, keep it.
  if (
    window.SVPlayer &&
    window.SVPlayer.audioElement instanceof HTMLAudioElement
  ) {
    return __svGlobalPlayerApp;
  }

  console.log(
    "[SVPlayer] ensureGlobalPlayer: bootstrapping createPlayerApp()…"
  );
  const app = createPlayerApp(root);
  if (app && app.audioElement) {
    registerGlobalPlayer(app);
    window.__SV_GLOBAL_PLAYER_INIT_DONE = true;
    return app;
  }

  console.warn(
    "[SVPlayer] ensureGlobalPlayer: player DOM present but createPlayerApp() failed or has no audio."
  );
  return null;
}

/**
 * Internal wiring: take the DOM-focused `app` from createPlayerApp()
 * and expose a stable, DOM-agnostic public API as window.SVPlayer.
 *
 * Public SVPlayer surface (keep this stable):
 *
 * - Properties:
 *   - audioElement: HTMLAudioElement (getter)
 *
 * - Playback controls:
 *   - play(): Promise<void> | void
 *   - pause(): void
 *   - isPlaying(): boolean
 *   - seek(seconds: number): void
 *
 * - Time helpers:
 *   - getCurrentTime(): number
 *   - getDuration(): number
 *
 * - Playlist / track:
 *   - getPlaylistLength(): number
 *   - getPlaylist(): Track[]
 *   - getAlbumTracklist(): Track[] | null
 *   - getPlaylistIndex(): number
 *   - getCurrentTrack(): Track | null
 *   - loadTrack(track, options?): void
 *   - loadPlaylist(tracks, options?): void
 *
 * - Events:
 *   - on(eventName, handler): void
 *   - off(eventName, handler): void
 *
 * - Page integration:
 *   - configureFromPage(): void
 *   - syncNowPlayingUi(): void
 */
function registerGlobalPlayer(app) {
  /**
   * Attach a simple global API so navigation / other scripts
   * never have to touch the raw <audio> element directly.
   */
  if (!app || !app.audioElement) {
    console.warn(
      "[Player] No audioElement found in app; cannot register global API."
    );
    return;
  }

  const audio = app.audioElement;

  // ------------------------------------------------------------
  // 7.1 Core playback & playlist state
  // ------------------------------------------------------------
  // Playlist / playback state
  // - playlist: the player's "autoPlaylist" queue (what the player is actually using)
  // - albumTracklist: the current page's album/single track list (what the page is showing)
  let playlist = [];
  let albumTracklist = null;
  let currentIndex = -1;
  let lastLoadedTrack = null; // tracks the last track we actually loaded into audio.src

  // Media Session helpers are defined at top-level (see Section 2).

  function syncNowPlayingUi() {
    if (!app) return;

    const track =
      window.SVPlayer && typeof window.SVPlayer.getCurrentTrack === "function"
        ? window.SVPlayer.getCurrentTrack()
        : null;

    //DEBUG
    svDebug("syncNowPlayingUi", {
      trackId: track && track.id,
      slug: track && (track.slug || track.songSlug),
      audioSrc:
        (app.audioElement && app.audioElement.currentSrc) ||
        app.audioElement.src ||
        null,
    });

    const isPlaying = !!(audio && !audio.paused && !audio.ended);

    if (typeof app.updateDesktopTitleFromTrack === "function") {
      app.updateDesktopTitleFromTrack(track);
    }
    if (typeof app.updateDesktopArtFromTrack === "function") {
      app.updateDesktopArtFromTrack(track);
    }

    // Album page: keep .album-track--active in sync with whatever's actually playing
    try {
      if (typeof syncAlbumActiveToCurrentAudio === "function") {
        syncAlbumActiveToCurrentAudio();
      }
    } catch (err) {
      console.warn("[SVPlayer] Failed to sync album active row:", err);
    }

    // NEW: keep the song-page hero button in sync with the global player
    if (
      typeof window !== "undefined" &&
      typeof window.svSyncSongHeroButton === "function"
    ) {
      try {
        window.svSyncSongHeroButton(track);
      } catch (err) {
        console.warn("[SVPlayer] Failed to sync song hero button:", err);
      }
    }

    // 🔹 Drawer “Now playing” indicator / meta
    // Always update the drawer, even on pages without a hero.
    updateDrawerFromTrackUi(track);

    // Lyrics: (re)wire synced lyrics for the current page & track
    try {
      reinitLyricsSyncForCurrentPage(app.audioElement);
    } catch (err) {
      console.warn("[Lyrics] Failed to reinit lyrics sync:", err);
    }

    // 🔹 New: update the "Now playing" shortcut in the drawer
    updateNowPlayingShortcut(track);

    // 🔹 Keep OS-level metadata (lockscreen / car) in sync
    updateMediaSession(track, isPlaying);
  }

  function loadTrackInternal(track, options = {}) {
    /**
     * Minimal helper: load a single track config into the existing audio element.
     * Expects track.audioUrl to be a valid URL/path.
     */
    if (!track || !track.audioUrl) {
      console.warn(
        "[SVPlayer] loadTrackInternal: missing track or audioUrl",
        track
      );
      return;
    }

    // Remember this as the last track we actually loaded
    lastLoadedTrack = track;

    // Set the src to the track's audio URL
    if (audio.src !== track.audioUrl) {
      audio.src = track.audioUrl;
    }

    // Optionally reset to the beginning
    if (options.reset === true) {
      try {
        audio.currentTime = 0;
      } catch (err) {
        console.warn("[SVPlayer] Failed to reset currentTime:", err);
      }
    }

    if (options.autoplay) {
      audio.play().catch((err) => {
        console.warn("[SVPlayer] Autoplay failed:", err);
      });
    }
  }

function pauseOtherMediaElements(mainAudio) {
  // 1) Native <audio>/<video> elements
  try {
    const mediaEls = document.querySelectorAll("audio, video");
    mediaEls.forEach((el) => {
      if (!(el instanceof HTMLMediaElement)) return;
      if (el === mainAudio) return;
      if (el.paused || typeof el.pause !== "function") return;

      try {
        el.pause();
      } catch {
        // Never let a broken <audio>/<video> throw here
      }
    });
  } catch {
    // Swallow any querySelector errors silently
  }

  // 2) lite-youtube embeds that expose the JS API
  try {
    const liteEls = document.querySelectorAll("lite-youtube");

    liteEls.forEach((el) => {
      const getYTPlayer = el.getYTPlayer;
      if (typeof getYTPlayer !== "function") {
        // This lite-youtube instance doesn't expose the JS API
        return;
      }

      // getYTPlayer() returns a Promise<YT.Player>
      try {
        const maybePromise = getYTPlayer.call(el);

        if (maybePromise && typeof maybePromise.then === "function") {
          maybePromise
            .then((player) => {
              try {
                if (player && typeof player.pauseVideo === "function") {
                  player.pauseVideo();
                }
              } catch (err) {
                console.warn(
                  "[SVPlayer] Failed to pause YouTube player via lite-youtube:",
                  err
                );
              }
            })
            .catch(() => {
              // ignore failures; don't block audio playback
            });
        }
      } catch (err) {
        console.warn(
          "[SVPlayer] Error calling lite-youtube.getYTPlayer():",
          err,
          el
        );
      }
    });
  } catch (err) {
    console.warn("[SVPlayer] Error querying lite-youtube elements:", err);
  }
}


  /**
   * Public SVPlayer API (exposed on window.SVPlayer)
   *
   * This is the stable interface other scripts should use.
   *
   * NOTE: If you change this shape, update:
   * - src/index.js imports
   * - Any PHP/JS that calls window.SVPlayer.* directly
   *
   * Methods:
   *   - audioElement: HTMLAudioElement
   *
   *   - play(): Promise<void> | void
   *   - pause(): void
   *   - isPlaying(): boolean
   *
   *   - getCurrentTime(): number
   *   - getDuration(): number
   *   - seek(seconds: number): void
   *
   *   - getPlaylist(): Track[]
   *   - getPlaylistLength(): number
   *   - getPlaylistIndex(): number
   *   - getAlbumTracklist(): Track[] | null
   *   - getCurrentTrack(): Track | null
   *
   *   - loadTrack(track, options?): void
   *     options:
   *       - autoplay?: boolean
   *       - reset?: boolean  // reset queue + index
   *
   *   - loadPlaylist(tracks, options?): void
   *     options:
   *       - startIndex?: number
   *       - autoplay?: boolean
   *       - load?: boolean  // just set queue, don't load into <audio> yet
   *
   *   - on(eventName, handler): void
   *   - off(eventName, handler): void
   *
   *   - configureFromPage(): void
   *   - syncNowPlayingUi(): void
   */

  const api = {
    /** The underlying HTMLAudioElement (read-only) */
    get audioElement() {
      return audio;
    },

    /** Number of tracks in the current playlist (0 if none) */
    getPlaylistLength() {
      return Array.isArray(playlist) ? playlist.length : 0;
    },

    /** Start playback (returns the play() promise) */
    play() {
      const hasSrc = !!(audio.currentSrc || audio.src);

      // Pause any other <audio>/<video> elements on the page
      // before starting or resuming the global player.
      pauseOtherMediaElements(audio);

      // Case 1: already have a loaded track → just play it
      if (hasSrc) {
        return audio.play();
      }

      // Case 2: no src yet, but we DO have a playlist (album page)
      if (Array.isArray(playlist) && playlist.length > 0) {
        let idx =
          typeof currentIndex === "number" &&
          currentIndex >= 0 &&
          currentIndex < playlist.length
            ? currentIndex
            : 0;

        const track = playlist[idx];
        if (!track || !track.audioUrl) {
          console.warn(
            "[SVPlayer] play(): playlist has no valid track at index",
            idx
          );
          return Promise.resolve();
        }

        console.log("[SVPlayer] play(): bootstrapping playback from playlist", {
          index: idx,
          trackId: track.id,
        });

        // Delegate to the existing loadTrack logic so playlist/currentIndex
        // and UI stay in sync.
        api.loadTrack(track, {
          reset: true,
          autoplay: true,
        });

        return Promise.resolve();
      }

      // Case 3: nothing to play (e.g., /music index)
      console.log(
        "[SVPlayer] play(): no loaded track and no playlist; ignoring request."
      );
      return Promise.resolve();
    },

    /** Pause playback */
    pause() {
      audio.pause();
    },

    /** True if audio is currently playing */
    isPlaying() {
      return !audio.paused;
    },

    /** Seek to a position in seconds */
    seek(seconds) {
      if (typeof seconds !== "number" || !Number.isFinite(seconds)) return;
      try {
        audio.currentTime = Math.max(0, seconds);
      } catch (err) {
        console.warn("[SVPlayer] Failed to seek:", err);
      }
    },

    /** Current playback time in seconds */
    getCurrentTime() {
      return audio.currentTime || 0;
    },

    /** Duration in seconds (0 if unknown) */
    getDuration() {
      return Number.isFinite(audio.duration) ? audio.duration : 0;
    },

    /** Basic event helpers around the audio element */
    on(eventName, handler) {
      audio.addEventListener(eventName, handler);
    },

    off(eventName, handler) {
      audio.removeEventListener(eventName, handler);
    },

    /** Sync UI (desktop title, drawer, etc.) to the current track */
    // inside api
    // syncNowPlayingUi() {
    //   syncNowPlayingUi();
    // },

    /**
     * Public: load a single track config.
     */
    loadTrack(track, options = {}) {
      //DEBUG
      svDebug("SVPlayer.loadTrack:start", {
        trackId: track && track.id,
        slug: track && (track.slug || track.songSlug),
        audioUrl: track && track.audioUrl,
        options,
        playlistLenBefore: Array.isArray(playlist) ? playlist.length : 0,
        currentIndexBefore: currentIndex,
      });

      console.log("[SVPlayer] loadTrack called", {
        trackId: track && track.id,
        title:
          (track && (track.songTitle || track.title || track.name)) || null,
      });

      // 1) Actually load + play the audio
      loadTrackInternal(track, options);

      // 2) Keep playlist/currentIndex in sync with this track
      if (track) {
        if (Array.isArray(playlist) && playlist.length > 0 && track.id) {
          const idx = playlist.findIndex((t) => t && t.id && t.id === track.id);

          if (idx !== -1) {
            // Track is in the existing playlist – just move the pointer
            currentIndex = idx;
          } else {
            // Track is *not* in the playlist – make it a single-track playlist
            playlist = [track];
            currentIndex = 0;
          }
        } else {
          // No existing playlist – start one with this track
          playlist = [track];
          currentIndex = 0;
        }
      }

      // 2.5) Update the drawer to show the current queue
      renderDrawerPlaylist(playlist);

      // 3) Centralized UI sync (title, art, drawer)
      syncNowPlayingUi();

      // 4) Play button enabled/disabled state
      if (app && typeof app.syncPlayButtonEnabledState === "function") {
        app.syncPlayButtonEnabledState();
      }
    },
    /**
     * Playlist API
     */
    loadPlaylist(tracks, options = {}) {
      //DEBUG
      svDebug("SVPlayer.loadPlaylist:start", {
        tracksLen: Array.isArray(tracks) ? tracks.length : null,
        options,
      });

      if (!Array.isArray(tracks) || tracks.length === 0) {
        console.warn("[SVPlayer] loadPlaylist: empty or invalid tracks array");
        return;
      }

      playlist = tracks;
      const idx =
        typeof options.startIndex === "number"
          ? Math.min(Math.max(options.startIndex, 0), tracks.length - 1)
          : 0;

      currentIndex = idx;

      //DEBUG
      svDebug("SVPlayer.loadPlaylist:afterAssign", {
        playlistLen: playlist.length,
        currentIndex,
        startTrackId: playlist[currentIndex] && playlist[currentIndex].id,
        startSlug:
          playlist[currentIndex] &&
          (playlist[currentIndex].slug || playlist[currentIndex].songSlug),
      });

      // Rebuild the drawer based on the new playlist
      renderDrawerPlaylist(playlist);

      // If we're supposed to actually start playback from this playlist,
      // load the selected track into the audio element and sync all UI.
      if (options.load !== false) {
        const track = playlist[currentIndex];
        if (track) {
          loadTrackInternal(track, {
            autoplay: options.autoplay !== false,
            reset: true,
          });

          // 🔸 This is the missing piece for album-row clicks:
          // we changed the track; now update drawer/album/hero/bars.
          syncNowPlayingUi();
        }
      }

      // Whether we loaded immediately or not, the playlist exists now,
      // so the play button may become enabled (album page case).
      if (app && typeof app.syncPlayButtonEnabledState === "function") {
        app.syncPlayButtonEnabledState();
      }
    },

    next() {
      //DEBUG
      svDebug("SVPlayer.next:start", {
        playlistLen: Array.isArray(playlist) ? playlist.length : 0,
        currentIndex,
      });

      if (!Array.isArray(playlist) || !playlist.length) return;
      if (currentIndex < 0) return;
      if (currentIndex >= playlist.length - 1) {
        // End of playlist (no loop)
        return;
      }

      currentIndex += 1;
      const track = playlist[currentIndex];

      //DEBUG
      svDebug("SVPlayer.next:afterIndex", {
        currentIndex,
        trackId: track && track.id,
        slug: track && (track.slug || track.songSlug),
        audioUrl: track && track.audioUrl,
      });

      if (!track) return;

      loadTrackInternal(track, { autoplay: true, reset: true });

      // 🔸 We just changed tracks, so sync drawer/album/hero/bars.
      syncNowPlayingUi();

      if (app && typeof app.syncPlayButtonEnabledState === "function") {
        app.syncPlayButtonEnabledState();
      }
    },

    previous() {
      //DEBUG
      svDebug("SVPlayer.previous:start", {
        playlistLen: Array.isArray(playlist) ? playlist.length : 0,
        currentIndex,
      });

      if (!Array.isArray(playlist) || !playlist.length) return;
      if (currentIndex <= 0) return;

      currentIndex -= 1;
      const track = playlist[currentIndex];
      if (!track) return;

      //DEBUG
      svDebug("SVPlayer.previous:afterIndex", {
        currentIndex,
        trackId: track && track.id,
        slug: track && (track.slug || track.songSlug),
        audioUrl: track && track.audioUrl,
      });

      loadTrackInternal(track, { autoplay: true, reset: true });

      // 🔸 Same here.
      syncNowPlayingUi();

      if (app && typeof app.syncPlayButtonEnabledState === "function") {
        app.syncPlayButtonEnabledState();
      }
    },

    getPlaylist() {
      return playlist.slice();
    },

    /** Per-page album/single track list for the current page context. */
    getAlbumTracklist() {
      return Array.isArray(albumTracklist) ? albumTracklist.slice() : [];
    },

    getPlaylistIndex() {
      return currentIndex;
    },

    getCurrentTrack() {
      // Canonical: what is the audio element actually playing?
      const rawSrc = (audio.currentSrc || audio.src || "")
        .split(/[?#]/)[0]
        .trim();

      //DEBUG
      svDebug("getCurrentTrack:start", {
        rawSrc,
        playlistLen: Array.isArray(playlist) ? playlist.length : 0,
        lastLoadedId: lastLoadedTrack && lastLoadedTrack.id,
        lastLoadedSlug:
          lastLoadedTrack && (lastLoadedTrack.slug || lastLoadedTrack.songSlug),
      });

      // If we have a loaded src, prefer that as the truth.
      if (rawSrc) {
        // 1) Try to match it within the current playlist
        if (Array.isArray(playlist) && playlist.length > 0) {
          const match = playlist.find((t) => {
            if (!t) return false;
            const url = (t.audioUrl || (t.links && t.links.mp3) || "")
              .split(/[?#]/)[0]
              .trim();
            return url && url === rawSrc;
          });

          //   if (match) {
          //     return match;
          //   }
          // }
          //DEBUG
          if (match) {
            svDebug("getCurrentTrack:matchedInPlaylist", {
              matchId: match.id,
              matchSlug: match.slug || match.songSlug,
              matchAudioUrl: match.audioUrl,
            });
            return match;
          }
        }

        // 2) Fall back to whatever we last loaded into audio.src
        // if (lastLoadedTrack) {
        //   return lastLoadedTrack;
        // }
        //DEBUG
        if (lastLoadedTrack) {
          svDebug("getCurrentTrack:lastLoadedFallback", {
            id: lastLoadedTrack.id,
            slug: lastLoadedTrack.slug || lastLoadedTrack.songSlug,
            audioUrl: lastLoadedTrack.audioUrl,
          });
          return lastLoadedTrack;
        }

        svDebug("getCurrentTrack:noMatchForRawSrc", { rawSrc });

        return null;
      }

      // No src loaded yet → nothing is actually playing.
      // We intentionally do NOT return playlist[currentIndex] here,
      // so album/song pages don't pretend track 0 is "now playing".
      svDebug("getCurrentTrack:noSrcLoaded", {});

      return null;
    },

    /**
     * Read per-page config from a <script> tag:
     *   <script type="application/json" data-sv-player-config>...</script>
     *
     * - On song pages: expects { track, autoplay }
     * - On album pages: expects { playlist, currentIndex, autoplay }
     */
    configureFromPage() {
      const script = document.querySelector(
        'script[type="application/json"][data-sv-player-config]'
      );
      if (!script) return;

      let config;
      try {
        config = JSON.parse(script.textContent || "{}");
      } catch (err) {
        console.warn("[SVPlayer] Failed to parse page config JSON:", err);
        return;
      }

      // Album page: playlist → becomes albumTracklist, MAY become the active autoPlaylist
      if (Array.isArray(config.playlist) && config.playlist.length > 0) {
        // Always remember what this page's tracklist is.
        albumTracklist = config.playlist.slice();

        const desiredIndex =
          typeof config.currentIndex === "number" ? config.currentIndex : 0;

        // What's *actually* playing right now?
        const currentTrack =
          typeof this.getCurrentTrack === "function"
            ? this.getCurrentTrack()
            : null;
        const hasSomethingPlaying = !!currentTrack;

        // Case 1: nothing playing yet → adopt this albumTracklist as the autoPlaylist
        if (!hasSomethingPlaying) {
          this.loadPlaylist(albumTracklist, {
            startIndex: desiredIndex,
            autoplay: !!config.autoplay,
            load: false, // don't touch audio.src on album page init
          });

          if (app && typeof app.syncPlayButtonEnabledState === "function") {
            app.syncPlayButtonEnabledState();
          }

          return;
        }

        // Case 2: something IS playing – adopt only if that track is part of this albumTracklist
        if (currentTrack && currentTrack.id) {
          const matchIndex = albumTracklist.findIndex(
            (t) => t && t.id && t.id === currentTrack.id
          );

          if (matchIndex !== -1) {
            this.loadPlaylist(albumTracklist, {
              startIndex: matchIndex,
              autoplay: false,
              load: false, // keep current audio.src, just align queue + index
            });

            if (app && typeof app.syncPlayButtonEnabledState === "function") {
              app.syncPlayButtonEnabledState();
            }

            return;
          }
        }

        // Case 3: something else is playing (different album / context)
        // - Do NOT change playlist/currentIndex
        // - Keep albumTracklist for clicks only
        return;
      }

      // Song page: single track
      if (config.track) {
        const track = config.track;
        const isPlaying = !audio.paused && !audio.ended;
        const hasPlaylist = Array.isArray(playlist) && playlist.length > 0;

        // Truly "fresh" case: no playlist yet AND nothing is playing.
        // In that scenario, let the song page take over and seed a
        // single-track playlist so the global play button works.
        if (!isPlaying && !hasPlaylist) {
          playlist = [track];
          currentIndex = 0;

          // 🔹 NEW: build the drawer list for this single-track playlist
          renderDrawerPlaylist(playlist);

          loadTrackInternal(track, {
            autoplay: !!config.autoplay,
            reset: true,
          });

          // UI + buttons should reflect this page's track now
          syncNowPlayingUi();

          if (app && typeof app.syncPlayButtonEnabledState === "function") {
            app.syncPlayButtonEnabledState();
          }

          return;
        }

        // Otherwise (we already have a playlist or something is playing),
        // leave the existing queue alone.
        //
        // The hero/drawer/album clicks will call SVPlayer.loadTrack(track),
        // and loadTrack() will:
        //   - move currentIndex if this track exists in the current playlist, or
        //   - fall back to a single-track playlist if it doesn’t.
      }
    },
    // Allow other scripts / DOM events to refresh all "now playing" UI
    syncNowPlayingUi() {
      syncNowPlayingUi();
    },
  };

  __svGlobalPlayerApp = app;
  // Expose on window for other scripts (and DevTools) to use.
  window.SVPlayer = api;
  // Keep Media Session in sync with actual audio events

  // Keep Media Session in sync with actual audio events
  setupMediaSessionSync(audio);

  // One-time setup for the drawer: clicks + initial render
  initDrawerInteractions();
  renderDrawerPlaylist(playlist);
}

export function autoInitSlimVolumePlayer() {
  /**
   * Legacy DOM auto-init entry point.
   *
   * Now delegates to ensureGlobalPlayer(), and only sets the global guard
   * once we actually have a real player.
   */
  // If a working global player already exists, do nothing.
  if (
    window.__SV_GLOBAL_PLAYER_INIT_DONE &&
    window.SVPlayer &&
    window.SVPlayer.audioElement instanceof HTMLAudioElement
  ) {
    console.debug("[Player] autoInit: player already initialized.");
    return __svGlobalPlayerApp;
  }

  const audioPlayer = document.getElementById("audioPlayer");
  if (!audioPlayer) {
    console.warn("[Player] #audioPlayer root not found; aborting init.");
    return null;
  }

  const app = ensureGlobalPlayer();
  if (app && app.audioElement) {
    window.__SV_GLOBAL_PLAYER_INIT_DONE = true;
    return app;
  }

  // IMPORTANT: do NOT "lock" the guard if there was no audio.
  // This lets a later album/song page (with real audio) bootstrap correctly.
  console.warn("[Player] autoInit: no playable audio; leaving guard unset.");
  window.__SV_GLOBAL_PLAYER_INIT_DONE = false;
  return null;
}

/**
 * Slim Volume audio player bootstrap.
 *
 * Responsibilities:
 * - Wire up the fixed bottom player (desktop + mobile layouts)
 * - Attach play/pause, prev/next, seek, and time display
 * - Optionally initialize Butterchurn visualizer (via toggle)
 * - Optionally initialize lyrics sync (JSON-backed, in-article)
 */

/**
 * App-style entry point: boot a Slim Volume player on a given root element.
 * Existing pages still rely on autoInitSlimVolumePlayer(), which delegates here.
 *
 * @param {HTMLElement} rootEl
 * @param {Object} [config]
 * @returns {{ audioElement: HTMLAudioElement } | null}
 */

// =========================
// Global player wrapper
// =========================

// Internal reference, so we can return the existing app if needed.
let __svGlobalPlayerApp = null;

// ------------------------------------------------------------
// 2. Media Session integration
// ------------------------------------------------------------

/**
 * Update the OS-level Media Session metadata based on the current track.
 * Safe no-op when Media Session isn't available.
 */
function updateMediaSession(track, isPlaying) {
  if (typeof navigator === "undefined" || !("mediaSession" in navigator)) {
    return;
  }

  if (!track) {
    // Clear metadata if nothing is loaded
    try {
      navigator.mediaSession.metadata = null;
      navigator.mediaSession.playbackState = "none";
    } catch (err) {
      console.warn("[MediaSession] Failed to clear metadata:", err);
    }
    return;
  }

  const title = track.songTitle || track.title || track.name || track.id || "";

  const artist = track.artistName || track.artist || track.artist_title || "";

  const album = track.albumTitle || track.album || "";

  const artUrl =
    track.artUrl ||
    track.coverUrl ||
    (track.artwork && track.artwork.url) ||
    null;

  const artwork = artUrl
    ? [
        { src: artUrl, sizes: "96x96", type: "image/jpeg" },
        { src: artUrl, sizes: "192x192", type: "image/jpeg" },
        { src: artUrl, sizes: "512x512", type: "image/jpeg" },
      ]
    : [];

  try {
    if (typeof window !== "undefined" && "MediaMetadata" in window) {
      navigator.mediaSession.metadata = new window.MediaMetadata({
        title,
        artist,
        album,
        artwork,
      });
    }
  } catch (err) {
    console.warn("[MediaSession] Failed to set metadata:", err);
  }

  try {
    navigator.mediaSession.playbackState = isPlaying ? "playing" : "paused";
  } catch (err) {
    console.warn("[MediaSession] Failed to set playbackState:", err);
  }
}

/**
 * Attach listeners that keep Media Session in sync with a given <audio> element.
 * Safe no-op when Media Session isn't available.
 */
function setupMediaSessionSync(audio) {
  if (!audio) return;

  if (typeof navigator !== "undefined" && "mediaSession" in navigator) {
    const syncMediaSessionFromAudio = () => {
      let track = null;
      if (
        window.SVPlayer &&
        typeof window.SVPlayer.getCurrentTrack === "function"
      ) {
        try {
          track = window.SVPlayer.getCurrentTrack();
        } catch (err) {
          console.warn("[MediaSession] getCurrentTrack failed:", err);
        }
      }

      const isPlaying = !!audio && !audio.paused && !audio.ended;

      updateMediaSession(track, isPlaying);
    };

    audio.addEventListener("play", syncMediaSessionFromAudio);
    audio.addEventListener("pause", syncMediaSessionFromAudio);
    audio.addEventListener("ended", syncMediaSessionFromAudio);
    audio.addEventListener("loadedmetadata", syncMediaSessionFromAudio);
    audio.addEventListener("timeupdate", (event) => {
      // Optional: only occasionally; here we just keep state fresh
      // Without localStorage we don't need position, but this ensures
      // playbackState stays "playing" if needed.
      if (!audio.paused && !audio.ended) {
        syncMediaSessionFromAudio();
      }
    });
  }
}
